import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, MapPinned, RefreshCw, Crosshair, X, Home, Route, Compass } from "lucide-react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { RotaBadge } from "@/pages/entregas/entregasHelpers";
import { BairroPopupCard } from "./BairroPopupCard";
import { RegiaoLegenda } from "./RegiaoLegenda";
import { SEM_REGIAO_COR, ZONA_COR, ZONA_LABEL, coresPorRegiao, fmtKm, linhasZonas, rotulosZonas } from "./regiaoMapaHelpers";

const POLL_MS = 3000;

// Bloco 9 (Iter 99): mapa interativo — polígonos/marcadores dos bairros coloridos pela rota.
// Clique num bairro → escolher rotas (PUT /bairros/{id}/rotas). Modo "posicionar" → clique no mapa
// define lat/lng manual pra bairros não encontrados no OpenStreetMap.
// Iter 108: modo "Colorir por Região" (cor da região + círculo do Centro + divisórias N/S/L/O + fora da zona).
export function RotasMapa() {
  const { user, currentEmpresaId } = useAuth();
  const canEdit = hasPerm(user, "rotas", "a");
  const canEditRegiao = hasPerm(user, "bairros", "a");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);      // bairro selecionado (popup lateral)
  const [placing, setPlacing] = useState(null);        // bairro em modo "posicionar no mapa"
  const [highlight, setHighlight] = useState(null);    // rota_id (ou regiao_id / __sem__ / __fora__) destacada na legenda
  const [colorBy, setColorBy] = useState("rota");      // "rota" | "regiao"
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const fittedRef = useRef(false);
  const placingRef = useRef(null);
  placingRef.current = placing;
  const regiaoCores = useMemo(() => coresPorRegiao(data?.regioes || []), [data]);
  const trocarModo = (m) => { setColorBy(m); setHighlight(null); };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try { const r = await api.get("/rotas/mapa"); setData(r.data); }
    catch (e) { if (!silent) toast.error(e?.response?.data?.detail || "Erro ao carregar mapa"); }
    finally { if (!silent) setLoading(false); }
  }, []);
  useEffect(() => { fittedRef.current = false; load(); }, [load, currentEmpresaId]);

  // Polling enquanto o job de geocodificação roda
  const running = !!data?.geocode?.running;
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(t);
  }, [running, load]);

  // Init do mapa (vanilla Leaflet — mesmo padrão do Monitoramento)
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true }).setView([-15.78, -47.93], 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap contributors", maxZoom: 19 }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    map.on("click", (e) => {
      const b = placingRef.current;
      if (!b) return;
      api.put(`/bairros/${b.id}/geo`, { lat: e.latlng.lat, lng: e.latlng.lng })
        .then(() => { toast.success(`"${b.nome}" posicionado no mapa`); setPlacing(null); load(true); })
        .catch((err) => toast.error(err?.response?.data?.detail || "Erro ao posicionar"));
    });
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; layerRef.current = null; fittedRef.current = false; };
  }, [load]);

  // Redesenha camadas quando os dados mudam
  useEffect(() => {
    const map = mapRef.current, layer = layerRef.current;
    if (!map || !layer || !data) return;
    layer.clearLayers();
    const bounds = [];
    const porRegiao = colorBy === "regiao";
    data.bairros.forEach((b) => {
      if (b.status !== "ok" || b.lat == null) return;
      const rotas = b.rotas || [];
      let style, dim, label;
      if (porRegiao) {
        const cor = b.regiao_id ? (regiaoCores[b.regiao_id] || SEM_REGIAO_COR) : SEM_REGIAO_COR;
        dim = highlight && (highlight === "__fora__" ? !b.fora_da_zona : highlight === "__sem__" ? !!b.regiao_id : b.regiao_id !== highlight);
        style = { color: b.fora_da_zona ? "#0F172A" : cor, weight: b.fora_da_zona ? 3 : 2, dashArray: b.fora_da_zona ? "5 4" : null,
          fillColor: cor, fillOpacity: dim ? 0.06 : (b.regiao_id ? 0.4 : 0.15), opacity: dim ? 0.25 : 0.9 };
        const s = b.sugestao;
        label = `<b>${b.nome}</b>${b.entregas_hoje ? ` · ${b.entregas_hoje} hoje` : ""}<br/><span style="font-size:11px">Região: ${b.regiao_nome || "sem região"}`
          + (s ? `<br/>Sugerida pela posição: ${s.regiao_nome || s.zona_label} · ${fmtKm(s.dist_km)} do centro` : "")
          + (b.fora_da_zona ? `<br/><b style="color:#B45309">⚠ fora da zona sugerida</b>` : "") + "</span>";
      } else {
        const cor = rotas[0]?.cor || SEM_REGIAO_COR;
        dim = highlight && !rotas.some((r) => r.id === highlight);
        style = { color: rotas[1]?.cor || cor, weight: rotas.length > 1 ? 3 : 2, dashArray: rotas.length > 1 ? "6 4" : null,
          fillColor: cor, fillOpacity: dim ? 0.06 : (rotas.length ? 0.35 : 0.15), opacity: dim ? 0.25 : 0.9 };
        label = `<b>${b.nome}</b>${b.entregas_hoje ? ` · ${b.entregas_hoje} hoje` : ""}<br/><span style="font-size:11px">${rotas.length ? rotas.map((r) => r.nome).join(", ") : "Sem rota"}${b.source === "manual" ? " · posição manual" : ""}</span>`;
      }
      let shape;
      if (b.polygon) {
        shape = L.geoJSON(b.polygon, { style });
        try { bounds.push(shape.getBounds().getCenter()); } catch (err) { console.debug("bairro sem geometria válida", b?.nome, err?.message); }
      } else {
        shape = L.circleMarker([b.lat, b.lng], { ...style, radius: 10, fillOpacity: dim ? 0.15 : 0.7 });
        bounds.push([b.lat, b.lng]);
      }
      shape.bindTooltip(label, { sticky: true });
      shape.on("click", (ev) => { L.DomEvent.stopPropagation(ev); if (!placingRef.current) setSelected(b); });
      shape.addTo(layer);
    });
    // Modo Região: centro da cidade + círculo do raio do "Centro" + divisórias diagonais N/L/S/O
    const c = data.empresa?.centro_regioes;
    if (porRegiao && c?.lat != null) {
      const raio = data.empresa.raio_km || 2;
      L.circle([c.lat, c.lng], { radius: raio * 1000, color: ZONA_COR.centro, weight: 2, dashArray: "6 6", fill: false, opacity: 0.9, interactive: false }).addTo(layer);
      linhasZonas(c, raio).forEach((pts) => L.polyline(pts, { color: "#334155", weight: 1.5, dashArray: "4 6", opacity: 0.7, interactive: false }).addTo(layer));
      rotulosZonas(c, Math.max(raio + 3, 5)).forEach(({ zona, pos }) =>
        L.marker(pos, { interactive: false, icon: L.divIcon({ className: "", html: `<div style="color:${ZONA_COR[zona]};font-weight:800;font-size:12px;text-shadow:0 0 3px #fff,0 0 3px #fff;white-space:nowrap">${ZONA_LABEL[zona]}</div>`, iconSize: [60, 16], iconAnchor: [30, 8] }) }).addTo(layer));
      L.marker([c.lat, c.lng], { icon: L.divIcon({ className: "", html: `<div style="background:#fff;border:2px solid ${ZONA_COR.centro};border-radius:9999px;width:22px;height:22px;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,.35);font-size:12px">◎</div>`, iconSize: [22, 22], iconAnchor: [11, 11] }) })
        .bindTooltip(`<b>Centro da cidade</b><br/>raio do Centro: ${fmtKm(raio)}`).addTo(layer);
    }
    const p = data.empresa?.partida;
    if (p?.lat != null) {
      L.marker([p.lat, p.lng], { icon: L.divIcon({ className: "", html: '<div style="background:#EA580C;color:#fff;border-radius:9999px;width:28px;height:28px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);font-size:14px">🏠</div>', iconSize: [28, 28], iconAnchor: [14, 14] }) })
        .bindTooltip(`<b>Ponto de partida</b><br/>${p.endereco || ""}`).addTo(layer);
      bounds.push([p.lat, p.lng]);
    }
    if (!fittedRef.current) {
      if (bounds.length >= 2) { map.fitBounds(L.latLngBounds(bounds).pad(0.15)); fittedRef.current = true; }
      else if (bounds.length === 1) { map.setView(bounds[0], 13); fittedRef.current = true; }
      else if (data.empresa?.center) { map.setView([data.empresa.center.lat, data.empresa.center.lng], 12); fittedRef.current = true; }
    }
  }, [data, highlight, colorBy, regiaoCores]);

  const geocode = async (force = false) => {
    try {
      const r = await api.post("/rotas/mapa/geocode", { force });
      if (r.data.queued === 0 && !r.data.running) toast.info("Todos os bairros já estão geocodificados");
      else toast.success(`Geocodificando ${r.data.queued || r.data.total} bairro(s) — ~${Math.ceil((r.data.eta_s || 0) / 60) || 1} min`);
      load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao iniciar geocodificação"); }
  };

  const g = data?.geocode;
  const semCidade = data && !data.empresa?.cidade;
  const naoEncontrados = useMemo(() => (data?.bairros || []).filter((b) => b.status === "not_found" || b.status === "error"), [data]);
  const rotasCount = useMemo(() => {
    const m = {};
    (data?.bairros || []).forEach((b) => (b.rotas || []).forEach((r) => { m[r.id] = (m[r.id] || 0) + 1; }));
    return m;
  }, [data]);
  const regiaoCount = useMemo(() => {
    const m = {};
    (data?.bairros || []).forEach((b) => { if (b.regiao_id) m[b.regiao_id] = (m[b.regiao_id] || 0) + 1; });
    return m;
  }, [data]);
  const semRegiaoCount = useMemo(() => (data?.bairros || []).filter((b) => !b.regiao_id).length, [data]);

  return (
    <div className="space-y-3" data-testid="rotas-mapa">
      {semCidade && (
        <div className="p-3 bg-amber-50 border border-amber-300 rounded text-sm text-amber-800" data-testid="mapa-sem-cidade">
          Informe <b>Cidade</b> e <b>UF</b> em <b>Empresa → Endereço</b> para o mapa localizar os bairros no OpenStreetMap.
        </div>
      )}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5" role="group" aria-label="Colorir por" data-testid="mapa-color-by">
            <span className="text-[11px] font-semibold text-muted-foreground self-center pl-2 pr-1">Colorir por</span>
            <button type="button" onClick={() => trocarModo("rota")} aria-pressed={colorBy === "rota"} data-testid="mapa-color-rota"
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${colorBy === "rota" ? "bg-orange-600 text-white" : "hover:bg-orange-50 text-slate-700"}`}>
              <Route className="w-3.5 h-3.5" />Rota
            </button>
            <button type="button" onClick={() => trocarModo("regiao")} aria-pressed={colorBy === "regiao"} data-testid="mapa-color-regiao"
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${colorBy === "regiao" ? "bg-orange-600 text-white" : "hover:bg-orange-50 text-slate-700"}`}>
              <Compass className="w-3.5 h-3.5" />Região
            </button>
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap" data-testid="mapa-geocode-status">
            {g && (<>
              <span><b>{g.ok}</b>/{g.total} bairros localizados</span>
              {g.pending > 0 && <span className="text-amber-700">{g.pending} pendente(s)</span>}
              {g.not_found > 0 && <span className="text-rose-700">{g.not_found} não encontrado(s)</span>}
              {g.running && <span className="inline-flex items-center gap-1 text-sky-700"><Loader2 className="w-3 h-3 animate-spin" />geocodificando {g.job_done}/{g.job_total}…</span>}
            </>)}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => load()} disabled={loading} data-testid="mapa-reload"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
          {canEdit && (
            <Button size="sm" onClick={() => geocode(false)} disabled={running || semCidade} className="bg-orange-600 hover:bg-orange-700" data-testid="mapa-geocode-btn">
              <MapPinned className="w-4 h-4 mr-2" />{g?.pending ? `Localizar ${g.pending} bairro(s)` : "Localizar bairros"}
            </Button>
          )}
          {canEdit && g?.not_found > 0 && !running && (
            <Button size="sm" variant="outline" onClick={() => geocode(true)} data-testid="mapa-geocode-retry">Tentar novamente ({g.not_found})</Button>
          )}
        </div>
      </div>

      {placing && (
        <div className="flex items-center justify-between p-2 rounded border border-sky-300 bg-sky-50 text-sm text-sky-800" data-testid="mapa-placing-banner">
          <span className="inline-flex items-center gap-2"><Crosshair className="w-4 h-4" />Clique no mapa para posicionar <b>{placing.nome}</b></span>
          <Button size="sm" variant="ghost" className="h-7" onClick={() => setPlacing(null)} data-testid="mapa-placing-cancel"><X className="w-4 h-4" /></Button>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_300px] gap-3">
        <Card className="overflow-hidden">
          <div ref={containerRef} style={{ height: 560 }} className={placing ? "cursor-crosshair" : ""} data-testid="mapa-container" />
        </Card>
        <div className="space-y-3">
          {colorBy === "regiao" ? (
            <RegiaoLegenda regioes={data?.regioes || []} cores={regiaoCores} counts={regiaoCount} semRegiao={semRegiaoCount} fora={data?.fora_da_zona || 0}
              raioKm={data?.empresa?.raio_km || 2} temCentro={!!data?.empresa?.centro_regioes} temPartida={!!data?.empresa?.partida} highlight={highlight} setHighlight={setHighlight} />
          ) : (
          <Card data-testid="mapa-legenda-rotas">
            <CardContent className="p-3 space-y-2">
              <div className="text-xs font-bold uppercase text-muted-foreground">Legenda</div>
              {(data?.rotas || []).map((r) => (
                <button type="button" key={r.id} onClick={() => setHighlight(highlight === r.id ? null : r.id)}
                  className={`w-full flex items-center justify-between gap-2 rounded px-2 py-1 text-left hover:bg-slate-50 ${highlight === r.id ? "bg-orange-50 ring-1 ring-orange-300" : ""}`}
                  data-testid={`mapa-legenda-${r.id}`}>
                  <RotaBadge rota={r} />
                  <span className="text-xs text-muted-foreground">{rotasCount[r.id] || 0} bairro(s)</span>
                </button>
              ))}
              <div className="flex items-center gap-2 px-2 text-xs text-muted-foreground"><span className="w-3 h-3 rounded-full bg-slate-300 inline-block" />Sem rota</div>
              {data?.empresa?.partida && <div className="flex items-center gap-2 px-2 text-xs text-muted-foreground"><Home className="w-3 h-3 text-orange-600" />Ponto de partida</div>}
              {(data?.rotas || []).length === 0 && <div className="text-xs text-muted-foreground px-2">Nenhuma rota cadastrada</div>}
            </CardContent>
          </Card>
          )}

          {selected && (
            <BairroPopupCard bairro={data.bairros.find((b) => b.id === selected.id) || selected} rotas={data.rotas} regioes={data.regioes || []} canEdit={canEdit} canEditRegiao={canEditRegiao}
              onClose={() => setSelected(null)} onSaved={() => load(true)} onPlace={() => { setPlacing(selected); setSelected(null); }} />
          )}

          {naoEncontrados.length > 0 && (
            <Card>
              <CardContent className="p-3 space-y-1">
                <div className="text-xs font-bold uppercase text-rose-700">Não localizados ({naoEncontrados.length})</div>
                <div className="max-h-48 overflow-y-auto divide-y">
                  {naoEncontrados.map((b) => (
                    <div key={b.id} className="flex items-center justify-between py-1 text-sm" data-testid={`mapa-nf-${b.id}`}>
                      <span className="truncate">{b.nome}</span>
                      {canEdit && <Button size="sm" variant="ghost" className="h-7 text-xs text-sky-700" onClick={() => setPlacing(b)} data-testid={`mapa-nf-place-${b.id}`}><Crosshair className="w-3 h-3 mr-1" />Posicionar</Button>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
